import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Video, Camera, Radio, Palmtree, GraduationCap, Truck, CalendarCheck,
  ArrowRight, MapPin, Phone, Mail, MessageCircle
} from 'lucide-react';
import { Footer } from '../../components/Footer';

const services = [
  {
    id: 'studio-rental',
    icon: Video,
    title: 'Studio Rental',
    subtitle: 'Hourly & Half/Full Day',
    description: 'Professional podcast and video studio with technician. 2-8 hour sessions with premium equipment.',
    price: 'From Rp 800,000/hour',
    link: '/services/studio-rental',
    featured: true
  },
  {
    id: 'photo-studio',
    icon: Camera,
    title: 'Photo Studio',
    subtitle: 'Visual Content',
    description: 'Professional headshots, personal brand photos, YouTube thumbnails, and course visuals.',
    price: 'From Rp 1,500,000',
    link: '/services/photo-studio'
  },
  {
    id: 'live-streaming',
    icon: Radio,
    title: 'Live Streaming',
    subtitle: 'Webinars & Events',
    description: 'Professional live streaming setup for webinars, launches, and online events.',
    price: 'From Rp 7,000,000',
    link: '/services/live-streaming'
  },
  {
    id: 'content-retreat',
    icon: Palmtree,
    title: 'Content Retreat',
    subtitle: '3-Day Intensive',
    description: 'Come to Bali for 3 days. Leave with 3 months of content produced.',
    price: 'From Rp 45,000,000',
    link: '/services/content-retreat'
  },
  {
    id: 'online-course',
    icon: GraduationCap,
    title: 'Online Course Filming',
    subtitle: 'Full Production',
    description: 'You have the knowledge. We handle everything else. Leave with your course ready to sell.',
    price: 'From Rp 35,000,000',
    link: '/services/online-course-filming'
  },
  {
    id: 'mobile-kit',
    icon: Truck,
    title: 'Mobile Equipment',
    subtitle: 'On-Location',
    description: 'Professional equipment rental with dedicated technician for off-site productions.',
    price: 'From Rp 6,500,000/day',
    link: '/services/mobile-kit'
  },
  {
    id: 'monthly-plans',
    icon: CalendarCheck,
    title: 'Monthly Plans',
    subtitle: 'Studio Subscriptions',
    description: 'Guaranteed studio time every month at up to 29% savings vs hourly rates.',
    price: 'From Rp 8,000,000/month',
    link: '/services/monthly-plans'
  }
];

const ServicesPage = () => {
  useEffect(() => {
    document.title = 'All Services | Canggu Podcast Studio';
    return () => { document.title = 'Canggu Podcast Studio'; };
  }, []);
  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#F5A623] text-sm tracking-[0.2em] font-medium mb-4 block">
              OUR SERVICES
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-6">
              One Studio.<br />Every Type of Content.
            </h1>
            <p className="text-gray-400 text-lg md:text-xl leading-relaxed">
              From podcasts to live webinars, brand photos to full online courses — 
              Canggu Studio handles it all.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <Link
                  key={service.id}
                  to={service.link}
                  className={`group relative p-6 border transition-all duration-300 hover:border-[#F5A623]/50 ${
                    service.featured 
                      ? 'border-[#F5A623] bg-[#F5A623]/5 md:col-span-2 lg:col-span-1' 
                      : 'border-[#262626] bg-[#121212]'
                  }`}
                  data-testid={`service-card-${service.id}`}
                >
                  {service.featured && (
                    <span className="absolute -top-3 left-6 px-3 py-1 bg-[#F5A623] text-black text-xs font-semibold">
                      MOST POPULAR
                    </span>
                  )}
                  
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${
                      service.featured ? 'bg-[#F5A623]/20' : 'bg-[#1a1a1a]'
                    }`}>
                      <Icon className={`w-6 h-6 ${
                        service.featured ? 'text-[#F5A623]' : 'text-gray-400 group-hover:text-[#F5A623]'
                      } transition-colors`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-heading font-semibold text-white mb-1 group-hover:text-[#F5A623] transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-sm text-[#F5A623] mb-2">{service.subtitle}</p>
                      <p className="text-sm text-gray-500 mb-4">{service.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-white font-semibold">{service.price}</span>
                        <ArrowRight className="w-5 h-5 text-gray-500 group-hover:text-[#F5A623] group-hover:translate-x-1 transition-all" />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#0A0A0A] border-t border-[#262626]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
            Not sure which service you need?
          </h2>
          <p className="text-gray-400 mb-8">
            Contact us and we'll help you find the perfect solution for your content goals.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://wa.me/628133975057"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-[#F5A623] text-black font-semibold hover:bg-[#FFB84D] transition-colors flex items-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Chat on WhatsApp
            </a>
            <a
              href="mailto:contact@canggupodcast.com"
              className="px-8 py-4 border border-[#262626] text-white font-semibold hover:border-[#F5A623] transition-colors flex items-center gap-2"
            >
              <Mail className="w-5 h-5" />
              Send Email
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ServicesPage;
